/*
 * <heritage-vault-button>
 *
 * Save-to-Vault toggle button. Reads product details from data-*
 * attributes, calls MDJVault.toggle on click, and reflects the
 * current saved state in its visual + ARIA state.
 *
 * Required attributes on the element:
 *   data-product-id, data-product-handle, data-product-title,
 *   data-product-url, data-product-image, data-product-era (opt),
 *   data-product-sku (opt)
 *
 * The element wraps the existing icon-button markup; the component
 * just attaches the toggle handler and swaps state classes. Visual
 * states are controlled by [data-saved="true|false"] on the host.
 */

class HeritageVaultButton extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleVaultChange = this.handleVaultChange.bind(this);
  }

  connectedCallback() {
    this.btn = this.querySelector('button, a');
    if (!this.btn) return;
    this.savedLabelEl = this.querySelector('[data-vault-label]');
    this.idleLabel = this.dataset.idleLabel || (this.savedLabelEl ? this.savedLabelEl.textContent : '');
    this.savedLabel = this.dataset.savedLabel || 'Saved to Vault';

    this.syncState();
    this.btn.addEventListener('click', this.handleClick);
    document.addEventListener('mdj:vault-changed', this.handleVaultChange);
  }

  disconnectedCallback() {
    if (this.btn) this.btn.removeEventListener('click', this.handleClick);
    document.removeEventListener('mdj:vault-changed', this.handleVaultChange);
  }

  productPayload() {
    return {
      id: this.dataset.productId,
      handle: this.dataset.productHandle,
      title: this.dataset.productTitle || '',
      image: this.dataset.productImage || '',
      era: this.dataset.productEra || '',
      sku: this.dataset.productSku || '',
      url: this.dataset.productUrl || '',
      // Canonical status at save time. The account-dashboard vault tab
      // uses this to paint the SOLD overlay without re-fetching every
      // card on render. It also re-syncs status asynchronously when the
      // dashboard mounts (see heritage-account-vault.js) so saved
      // pieces that go sold later still flip correctly.
      status: this.dataset.productStatus || 'available'
    };
  }

  handleClick(event) {
    event.preventDefault();
    if (!window.MDJVault) return;
    const item = this.productPayload();
    if (!item.id) return;
    window.MDJVault.toggle(item);
    this.syncState();
  }

  handleVaultChange() {
    this.syncState();
  }

  syncState() {
    if (!window.MDJVault || !this.dataset.productId) return;
    const isSaved = window.MDJVault.has(this.dataset.productId);
    this.dataset.saved = isSaved ? 'true' : 'false';
    if (this.btn) {
      this.btn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    }
    if (this.savedLabelEl) {
      this.savedLabelEl.textContent = isSaved ? this.savedLabel : this.idleLabel;
    }
  }
}

if (!customElements.get('heritage-vault-button')) {
  customElements.define('heritage-vault-button', HeritageVaultButton);
}
